import { VenueDetailsBySlugResponse, useI18N } from "common/hooks";
import { Helmet } from "react-helmet";
import { useMemo } from "react";
import { bucketUrlPrefix } from "common/config";
import { House, CaretRight } from "phosphor-react";
import { BusinessTab } from "common/types";
import AnalyticsService from "@/services/analytics";

type ComplexVenueTabsContentProps = {
  onClose: () => void;
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  businessTab: BusinessTab;
  onRefresh: () => void;
  cid?: string;
  tid?: string;
};

function ComplexVenueTabsContent({
  venueDetailsResponse: data,
  businessTab,
  onClose,
}: ComplexVenueTabsContentProps) {
  const i18n = useI18N();
  // Generate dynamic title based on the business tab
  const pageTitle = useMemo(() => {
    return `${businessTab.title} | ${data?.details?.name || "Venue"} | Sociability`;
  }, [businessTab.title, data?.details?.name]);

  // Sanitize HTML content for safe rendering
  const htmlContent = useMemo(() => {
    if (!businessTab.content) return "";
    // Basic sanitization - RECOMMENDED: Use DOMPurify for production-grade HTML sanitization
    // to prevent XSS attacks and ensure screen reader compatibility
    return businessTab.content
      .replace(/&nbsp;/g, "") // Replace &nbsp; with regular spaces
      .replace(/&amp;/g, "&") // Replace &amp; with &
      .replace(/&lt;/g, "<") // Replace &lt; with <
      .replace(/&gt;/g, ">") // Replace &gt; with >
      .replace(/&quot;/g, '"') // Replace &quot; with"
      .trim();
  }, [businessTab.content]);

  return (
    <div className="min-h-screen w-full">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {/* Breadcrumb Header */}
      <div className="flex items-center justify-end  max-w-[1440px] mx-auto w-full">
        <header className="self-stretch py-3 bg-surface-secondary border-b border-border-token-brand-minimal flex-shrink-0 max-w-[1440px] mx-auto w-full inline-flex flex-col justify-start items-start">
          {/* Breadcrumb Navigation */}
          <div className="self-stretch px-4 md:px-7 inline-flex justify-start items-start gap-2.5">
            <nav
              aria-label="Breadcrumb"
              className="flex-1 flex justify-start items-center min-w-0"
            >
              <ol className="flex items-center gap-2 list-none m-0 p-0">
                {/* Venue Name with Home Icon */}
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      // Track analytics
                      AnalyticsService.trackGAEventHandler("complexVenueTabBackToVenueClicked");
                      AnalyticsService.trackMx("complexVenueTabBackToVenueClicked", {
                        VenueId: data?.details?.business?.id,
                        VenueName: data?.details?.name,
                        TabId: businessTab?.id,
                        TabTitle: businessTab?.title,
                      });
                      
                      // Perform navigation
                      onClose();
                    }}
                    className="min-w-8 min-h-8 bg-white/0 rounded-lg flex items-center gap-1.5 text-fg-neutral-strong hover:text-fg-brand-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    aria-label={i18n.get("backToVenueDetails")}
                  >
                    <House size={20} className="text-fg-neutral-subtle"  weight="fill" />
                    <span className="text-center justify-center text-fg-neutral-strong label-m-medium">{data?.details?.name}</span>
                  </button>
                </li>
              </ol>
            </nav>
          </div>

          {/* Title Row */}
          <div className="self-stretch px-7 flex flex-col justify-start items-start">
            <div className="self-stretch inline-flex justify-start items-center gap-3">
              <h1 className="flex-1 justify-center text-fg-neutral-strong title-l-bold">
                {businessTab.title}
              </h1>
            </div>
          </div>
        </header>
      </div>    




      {/* Main Content */}
      <div className="bg-bg-brand-secondary-default max-w-[1440px] mx-auto px-4 md:px-6 relative pb-8 md:pb-12">
        <div className="pt-6 lg:pt-8">
          {/* Subtitle - if available */}
          {businessTab.subtitle && (
            <p className="label-m-medium text-fg-brand-strong mb-6">
              {businessTab.subtitle}
            </p>
          )}

          {/* Content Section */}
          {htmlContent && (
            <div className="prose prose-lg max-w-none mb-8">
              <div
                className="text-fg-neutral-strong leading-relaxed"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </div>
          )}

          {/* Images Section - Now below content */}
          {businessTab.images && businessTab.images.length > 0 && (
            <div className="mb-8">
              {businessTab.images.length === 1 ? (
                // Single image - full width
                <div className="w-full h-[400px] md:h-[500px] rounded-lg overflow-hidden">
                  <img
                    alt={businessTab.images[0].alt_text || businessTab.title}
                    className="w-full h-full object-cover"
                    src={bucketUrlPrefix + businessTab.images[0].image_path}
                    loading="lazy"
                  />
                </div>
              ) : (
                // Multiple images - grid layout
                <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {businessTab.images
                    .sort((a, b) => a.order - b.order)
                    .map((image) => (
                      <div
                        key={image.id}
                        className="h-[250px] md:h-[300px] rounded-lg overflow-hidden"
                      >
                        <img
                          alt={image.alt_text || businessTab.title}
                          className="w-full h-full object-cover"
                          src={bucketUrlPrefix + image.image_path}
                          loading="lazy"
                        />
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Fallback if no content */}
          {!htmlContent &&
            (!businessTab.images || businessTab.images.length === 0) && (
              <div className="text-center py-12">
                <h3 className="label-m-semibold text-gray-900 mb-2">
                  {i18n.get("noContentAvailable")}
                </h3>
                <p className="label-s-medium text-gray-600">
                  {i18n.get("sectionNoContentToDisplay")}
                </p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default ComplexVenueTabsContent;
